import { useEffect, useRef } from 'react';

/** One focus target per validation attempt; never jump between several invalid fields. */
export function focusFormError(node) {
  if (!node || !node.isConnected) return;
  node.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  node.focus?.({ preventScroll: true });
}

export default function useFormErrorFocus(rootRef, error, busy = false, selector = null, attempt = 0) {
  const queued = useRef(false);
  useEffect(() => {
    const root = rootRef.current;
    const clearCorrectedField = event => {
      const field = event.target;
      if (field.matches?.('[data-validation-error="true"]') && field.validity?.valid) {
        field.removeAttribute('data-validation-error');
        if (field.dataset.validationOwnsAria === 'true') {
          field.removeAttribute('aria-invalid');
          field.removeAttribute('data-validation-owns-aria');
        }
      }
    };
    root?.addEventListener('input', clearCorrectedField);
    root?.addEventListener('change', clearCorrectedField);
    return () => {
      root?.removeEventListener('input', clearCorrectedField);
      root?.removeEventListener('change', clearCorrectedField);
    };
  });
  useEffect(() => {
    if (!error || busy) return;
    const root = rootRef.current;
    const target = (selector && root?.querySelector(selector)) || root?.querySelector('[aria-invalid="true"], [data-invalid="true"] input, [role="alert"]');
    focusFormError(target);
  }, [rootRef, error, busy, selector, attempt]);

  function onInvalidCapture(event) {
    const field = event.target;
    field.setAttribute('data-validation-error', 'true');
    if (!field.hasAttribute('aria-invalid')) {
      field.setAttribute('aria-invalid', 'true');
      field.setAttribute('data-validation-owns-aria', 'true');
    }
    if (queued.current) return;
    queued.current = true;
    // Wait for the browser's constraint-validation pass to finish.
    window.setTimeout(() => { queued.current = false; focusFormError(field); }, 0);
  }
  return onInvalidCapture;
}
